import React, { useEffect, useState, useContext, useMemo } from "react";
import {
  Box,
  Button,
  Grid,
  Stack,
  TextField,
  MenuItem,
  Typography,
  IconButton,
  Chip,
  Divider,
  Checkbox,
  FormControlLabel,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
} from "@mui/material";
import SearchIcon from "@mui/icons-material/Search";
import { useForm, Controller } from "react-hook-form";
import { isMobile } from "react-device-detect";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import { CircularProgress } from "@mui/material";
import AttachFileIcon from "@mui/icons-material/AttachFile";
import VisibilityIcon from "@mui/icons-material/Visibility";
import DeleteIcon from "@mui/icons-material/Delete";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import CancelIcon from "@mui/icons-material/Cancel";
import { useNavigate } from "react-router-dom";
import axios from "../../axiosConfig";
import { ConfigLinksDetailsContext } from "../../Context/cofingLinksDetailsContext";
import PreviewAttachmentDialog from "../PreviewAttachmentDialog";
import MessageModal from "../MessageModal";

const EMAIL_REGEX = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

const validationFields = {
  0: [
    "businessLicenseNo",
    "companyNameA",
    "companyType",
    "companyNationality",
    "incorporationPlace",
    "mainMobile",
    "email",
  ],
  1: [],
};

const CompanySignupForm = ({
  activeStep,
  setActiveStep,
  stepsLength,
  allowCurrentUserDelegate = false,
  currentUserDelegate = null,
}) => {
  const [companyTypes, setCompanyTypes] = useState([]);
  const [companyNationalities, setCompanyNationalities] = useState([]);
  const [countries, setCountries] = useState([]);
  const [attachmentTypes, setAttachmentTypes] = useState([]);
  const [attachments, setAttachments] = useState([]); // [{typeId, typeName, fileObject}]
  const [selectedAttachType, setSelectedAttachType] = useState("");
  const [attachError, setAttachError] = useState("");
  const [isLoading, setIsLoading] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [openSuccessModal, setOpenSuccessModal] = useState(false);
  const [openFailureModal, setOpenFailureModal] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");
  const [stepError, setStepError] = useState("");
  const [previewFile, setPreviewFile] = useState(null); // {fileObject}

  // Owners (ملاك/مسؤولون)
  const [responsibles, setResponsibles] = useState([]); // [{citizenId, documentNumber, fullName, responsibleRole, partnershipPercentage, isMainContact}]
  const [respForm, setRespForm] = useState({ docNumber: "", searching: false, found: null, role: "", percentage: "", isMainContact: false, error: "" });

  // Delegates (مفوّضون)
  const [delegations, setDelegations] = useState([]); // [{delegateId, documentNumber, fullName}]
  const [delForm, setDelForm] = useState({ docNumber: "", searching: false, found: null, error: "", });
  const [includeCurrentUserDelegate, setIncludeCurrentUserDelegate] = useState(false);

  const { configLinks } = useContext(ConfigLinksDetailsContext);
  const navigate = useNavigate();

  const {
    handleSubmit,
    control,
    trigger,
    formState: { errors },
  } = useForm({ mode: "onChange" });

  const onLastFormPage = activeStep === 4;

  const effectiveDelegations = useMemo(() => {
    if (!allowCurrentUserDelegate || !includeCurrentUserDelegate || !currentUserDelegate?.delegateId) {
      return delegations;
    }

    if (delegations.some((d) => d.delegateId === currentUserDelegate.delegateId)) {
      return delegations;
    }

    return [
      {
        delegateId: currentUserDelegate.delegateId,
        documentNumber: currentUserDelegate.documentNumber,
        fullName: currentUserDelegate.fullName,
        fromCurrentUser: true,
      },
      ...delegations,
    ];
  }, [allowCurrentUserDelegate, includeCurrentUserDelegate, currentUserDelegate, delegations]);

  const { validFileTypes, fileTypeMessages } = useMemo(() => {
    const acceptPdfOnly = configLinks?.acceptPdfOnly ?? false;
    const allowImages = configLinks?.allowImages ?? true;
    const allowWordDocs = configLinks?.allowWordDocs ?? true;
    const baseMimes = new Set(["application/pdf", "image/jpeg"]);
    const baseMessages = ["PDF", "JPEG"];
    if (allowImages) {
      ["image/jpg", "image/png"].forEach((m) => baseMimes.add(m));
      baseMessages.push("JPG", "PNG");
    }
    if (allowWordDocs) {
      ["application/msword", "application/vnd.openxmlformats-officedocument.wordprocessingml.document"].forEach((m) => baseMimes.add(m));
      baseMessages.push("DOC", "DOCX");
    }
    return { validFileTypes: Array.from(baseMimes), fileTypeMessages: baseMessages };
  }, [configLinks]);

  useEffect(() => {
    setIsLoading(true);
    const fetchLookups = async () => {
      try {
        const [types, nationalities, ctrs, attTypes] = await Promise.all([
          axios.get("/lookup/CompanyTypes"),
          axios.get("/lookup/CompanyNationalities"),
          axios.get("/lookup/Countries"),
          axios.get("/lookup/AttachmentType"),
        ]);
        setCompanyTypes(types);
        setCompanyNationalities(nationalities);
        setCountries(ctrs);
        const allowedCompanyAttachmentTypeIds = Array.isArray(configLinks?.companyAttachmentTypeIds)
          ? configLinks.companyAttachmentTypeIds.map(Number)
          : [];
        const filteredAttachmentTypes = allowedCompanyAttachmentTypeIds.length
          ? attTypes.filter((t) => allowedCompanyAttachmentTypeIds.includes(Number(t.id)))
          : attTypes;
        setAttachmentTypes(filteredAttachmentTypes);
      } catch (e) {
        console.error(e);
      } finally {
        setIsLoading(false);
      }
    };
    fetchLookups();
  }, [configLinks]);

  const handleNext = async () => {
    setStepError("");
    if (activeStep === stepsLength - 1) {
      handleSubmit(onSubmit)();
      return;
    }
    if (activeStep === 0) {
      const result = await trigger(validationFields[0]);
      if (!result) return;
    }
    if (activeStep === 1) {
      if (responsibles.length === 0) {
        setStepError("يجب إضافة مالك أو مسؤول واحد على الأقل");
        return;
      }

      const mainContactCount = responsibles.filter((r) => r.isMainContact).length;
      if (mainContactCount !== 1) {
        setStepError("يجب تعيين جهة اتصال رئيسية واحدة فقط (لا أقل ولا أكثر)");
        return;
      }

      const totalPct = responsibles.reduce((sum, r) => sum + (r.partnershipPercentage ?? 0), 0);
      if (totalPct > 100) {
        setStepError(`مجموع نسب الشراكة (${totalPct}%) يتجاوز 100%. يرجى التصحيح قبل المتابعة.`);
        return;
      }
    }
    if (activeStep === 2) {
      if (effectiveDelegations.length === 0) {
        setStepError("يجب إضافة مفوّض واحد على الأقل");
        return;
      }
    }
    setActiveStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setStepError("");
    setActiveStep((prev) => prev - 1);
  };

  const onSubmit = async (data) => {
    try {
      setIsSubmitting(true);
      const formData = new FormData();

      formData.append("CompanyInfoDto.BusinessLicenseNo", data.businessLicenseNo);
      formData.append("CompanyInfoDto.CompanyType", data.companyType);
      formData.append("CompanyInfoDto.CompanyNameA", data.companyNameA);
      if (data.companyNameL?.trim())
        formData.append("CompanyInfoDto.CompanyNameL", data.companyNameL.trim());
      if (data.founditionDate)
        formData.append("CompanyInfoDto.FounditionDate", dayjs(data.founditionDate).toISOString());
      formData.append("CompanyInfoDto.CompanyNationality", data.companyNationality);
      formData.append("CompanyInfoDto.IncorporationPlace", data.incorporationPlace);
      if (data.city?.trim()) formData.append("CompanyInfoDto.City", data.city.trim());
      if (data.street?.trim()) formData.append("CompanyInfoDto.Street", data.street.trim());
      if (data.address?.trim()) formData.append("CompanyInfoDto.Address", data.address.trim());
      formData.append("CompanyInfoDto.MainMobile", data.mainMobile);
      if (data.telephone1?.trim()) formData.append("CompanyInfoDto.Telephone1", data.telephone1.trim());
      if (data.telephone2?.trim()) formData.append("CompanyInfoDto.Telephone2", data.telephone2.trim());
      if (data.fax?.trim()) formData.append("CompanyInfoDto.Fax", data.fax.trim());
      if (data.mailBox?.trim()) formData.append("CompanyInfoDto.MailBox", data.mailBox.trim());
      if (data.email?.trim()) formData.append("CompanyInfoDto.Email", data.email.trim());
      if (data.website?.trim()) formData.append("CompanyInfoDto.Website", data.website.trim());

      attachments.forEach((att, idx) => {
        formData.append(`Attachments[${idx}].File`, att.fileObject);
        formData.append(`Attachments[${idx}].AttachmentType`, att.typeId);
      });

      responsibles.forEach((r, idx) => {
        formData.append(`CompanyResponsibles[${idx}].CitizenId`, r.citizenId);
        if (r.responsibleRole) formData.append(`CompanyResponsibles[${idx}].ResponsibleRole`, r.responsibleRole);
        if (r.partnershipPercentage != null) formData.append(`CompanyResponsibles[${idx}].PartnershipPercentage`, r.partnershipPercentage);
        formData.append(`CompanyResponsibles[${idx}].IsMainContact`, r.isMainContact);
      });

      effectiveDelegations.forEach((d, idx) => {
        formData.append(`Delegations[${idx}].DelegateId`, d.delegateId);
      });

      await axios.post("company", formData);
      setOpenSuccessModal(true);
    } catch (e) {
      setErrorMessage(e?.response?.data?.message ?? "حدث خطأ أثناء إرسال الطلب، الرجاء المحاولة مرة أخرى");
      setOpenFailureModal(true);
      console.error(e);
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleFileSelected = (file) => {
    setAttachError("");
    if (!file) return;

    if (!selectedAttachType) {
      setAttachError("الرجاء اختيار نوع المرفق أولاً");
      return;
    }
    const maxSizeInMB = configLinks?.fileSizeLimitMb ?? 20;
    const maxSizeInBytes = maxSizeInMB * 1024 * 1024;
    if (!validFileTypes.includes(file.type)) {
      setAttachError(`الرجاء تحميل ملف صالح (${fileTypeMessages.join(", ")})`);
      return;
    }
    if (file.size > maxSizeInBytes) {
      setAttachError(`حجم الملف لا يمكن أن يتجاوز ${maxSizeInMB} MB`);
      return;
    }
    const typeId = Number(selectedAttachType);
    const typeName = attachmentTypes.find((t) => t.id === typeId)?.name ?? "مرفق";

    // Replace if same type already added, otherwise add new
    setAttachments((prev) => {
      const exists = prev.findIndex((a) => a.typeId === typeId);
      if (exists >= 0) {
        const updated = [...prev];
        updated[exists] = { typeId, typeName, fileObject: file };
        return updated;
      }
      return [...prev, { typeId, typeName, fileObject: file }];
    });
    setSelectedAttachType("");
    // reset file input
    document.getElementById("companyFileInput").value = "";
  };

  const removeAttachment = (typeId) => {
    setAttachments((prev) => prev.filter((a) => a.typeId !== typeId));
  };

  const searchCitizenForRole = async () => {
    if (!respForm.docNumber.trim()) return;
    setRespForm((p) => ({ ...p, searching: true, found: null, error: "" }));
    try {
      const data = await axios.get(`/company/citizen/${respForm.docNumber.trim()}`);
      if (responsibles.some((r) => r.citizenId === data.citizenId)) {
        setRespForm((p) => ({ ...p, searching: false, error: "هذا الشخص مضاف مسبقاً" }));
      } else {
        setRespForm((p) => ({ ...p, searching: false, found: data }));
      }
    } catch {
      setRespForm((p) => ({ ...p, searching: false, error: "لم يتم العثور على مواطن بهذا الرقم" }));
    }
  };

  const addResponsible = () => {
    if (!respForm.found) return;

    const alreadyHasMainContact = responsibles.some((r) => r.isMainContact);
    if (respForm.isMainContact && alreadyHasMainContact) {
      setRespForm((p) => ({
        ...p,
        error: "مسموح جهة اتصال رئيسية واحدة فقط. أزل الحالية أولاً إذا أردت تغييرها.",
      }));
      return;
    }

    setResponsibles((prev) => [
      ...prev,
      {
        citizenId: respForm.found.citizenId,
        documentNumber: respForm.found.documentNumber,
        fullName: respForm.found.fullName,
        responsibleRole: respForm.role || null,
        partnershipPercentage: respForm.percentage !== "" ? Number(respForm.percentage) : null,
        isMainContact: respForm.isMainContact,
      },
    ]);
    setRespForm({ docNumber: "", searching: false, found: null, role: "", percentage: "", isMainContact: false, error: "" });
  };

  const searchCitizenForDelegation = async () => {
    if (!delForm.docNumber.trim()) return;
    setDelForm((p) => ({ ...p, searching: true, found: null, error: "" }));
    try {
      const data = await axios.get(`/company/citizen/${delForm.docNumber.trim()}`);
      const isCurrentUser = currentUserDelegate?.delegateId === data.citizenId;
      const alreadyIncludedAsCurrent = isCurrentUser && allowCurrentUserDelegate && includeCurrentUserDelegate;
      if (delegations.some((d) => d.delegateId === data.citizenId) || alreadyIncludedAsCurrent) {
        setDelForm((p) => ({ ...p, searching: false, error: "هذا الشخص مضاف مسبقاً" }));
      } else {
        setDelForm((p) => ({ ...p, searching: false, found: data }));
      }
    } catch {
      setDelForm((p) => ({ ...p, searching: false, error: "لم يتم العثور على مواطن بهذا الرقم" }));
    }
  };

  const addDelegation = () => {
    if (!delForm.found) return;
    setDelegations((prev) => [
      ...prev,
      {
        delegateId: delForm.found.citizenId,
        documentNumber: delForm.found.documentNumber,
        fullName: delForm.found.fullName,
      },
    ]);
    setDelForm({ docNumber: "", searching: false, found: null, error: "" });
  };

  const SectionTitle = ({ title }) => (
    <Box sx={{ py: 1, mb: 1 }}>
      <Typography variant="h6" color="primary" sx={{ fontWeight: "bold" }}>{title}</Typography>
      <Divider sx={{ mt: 0.5 }} />
    </Box>
  );

  const FieldLabel = ({ label, required }) => (
    <Typography sx={{ fontWeight: "bold", mb: 1 }}>
      {label} {required && <span style={{ color: "red" }}>*</span>}
    </Typography>
  );

  if (isLoading) {
    return <Box sx={{ textAlign: "center", py: 8 }}><CircularProgress size={80} /></Box>;
  }

  return (
    <Box>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <form onSubmit={handleSubmit(onSubmit)} autoComplete="off">

          {/* ====== STEP 0 + Review: Company Info ====== */}
          <Box sx={{ display: activeStep === 0 || onLastFormPage ? "block" : "none" }}>

            <SectionTitle title="معلومات الشركة" />
            <Grid container spacing={2}>

              <Grid item md={3} xs={12}>
                <FieldLabel label="رقم السجل التجاري" required />
                <Controller name="businessLicenseNo" control={control}
                  rules={{ required: "هذا الحقل مطلوب" }}
                  render={({ field }) => (
                    <TextField {...field} disabled={onLastFormPage} fullWidth variant="outlined"
                      error={!!errors.businessLicenseNo} helperText={errors.businessLicenseNo?.message}
                      FormHelperTextProps={{ sx: { textAlign: "start" } }} />
                  )} />
              </Grid>

              <Grid item md={3} xs={12}>
                <FieldLabel label="نوع الشركة" required />
                <Controller name="companyType" control={control}
                  rules={{ required: "هذا الحقل مطلوب" }}
                  render={({ field }) => (
                    <TextField {...field} disabled={onLastFormPage} select fullWidth variant="outlined"
                      error={!!errors.companyType} helperText={errors.companyType?.message}
                      FormHelperTextProps={{ sx: { textAlign: "start" } }}>
                      {companyTypes.map((o) => (
                        <MenuItem key={o.id} value={o.id} sx={{ direction: "rtl" }}>{o.name}</MenuItem>
                      ))}
                    </TextField>
                  )} />
              </Grid>

              <Grid item md={3} xs={12}>
                <FieldLabel label="جنسية الشركة" required />
                <Controller name="companyNationality" control={control}
                  rules={{ required: "هذا الحقل مطلوب" }}
                  render={({ field }) => (
                    <TextField {...field} disabled={onLastFormPage} select fullWidth variant="outlined"
                      error={!!errors.companyNationality} helperText={errors.companyNationality?.message}
                      FormHelperTextProps={{ sx: { textAlign: "start" } }}>
                      {companyNationalities.map((o) => (
                        <MenuItem key={o.id} value={o.id} sx={{ direction: "rtl" }}>{o.name}</MenuItem>
                      ))}
                    </TextField>
                  )} />
              </Grid>

              <Grid item md={3} xs={12}>
                <FieldLabel label="مكان التأسيس" required />
                <Controller name="incorporationPlace" control={control}
                  rules={{ required: "هذا الحقل مطلوب" }}
                  render={({ field }) => (
                    <TextField {...field} disabled={onLastFormPage} select fullWidth variant="outlined"
                      error={!!errors.incorporationPlace} helperText={errors.incorporationPlace?.message}
                      FormHelperTextProps={{ sx: { textAlign: "start" } }}>
                      {countries.map((o) => (
                        <MenuItem key={o.id} value={o.id} sx={{ direction: "rtl" }}>{o.name}</MenuItem>
                      ))}
                    </TextField>
                  )} />
              </Grid>

              <Grid item md={3} xs={12}>
                <FieldLabel label="اسم الشركة بالعربي" required />
                <Controller name="companyNameA" control={control}
                  rules={{ required: "هذا الحقل مطلوب", minLength: { value: 2, message: "يجب ألا يقل عن حرفين" } }}
                  render={({ field }) => (
                    <TextField {...field} disabled={onLastFormPage} fullWidth variant="outlined"
                      error={!!errors.companyNameA} helperText={errors.companyNameA?.message}
                      FormHelperTextProps={{ sx: { textAlign: "start" } }} />
                  )} />
              </Grid>

              <Grid item md={3} xs={12}>
                <FieldLabel label="اسم الشركة بالإنجليزي" />
                <Controller name="companyNameL" control={control}
                  render={({ field }) => (
                    <TextField {...field} disabled={onLastFormPage} fullWidth variant="outlined" />
                  )} />
              </Grid>

              <Grid item md={3} xs={12}>
                <FieldLabel label="تاريخ التأسيس" />
                <Controller name="founditionDate" control={control}
                  render={({ field }) => (
                    <DatePicker {...field} disabled={onLastFormPage}
                      value={field.value ? dayjs(field.value) : null}
                      onChange={(val) => field.onChange(val)}
                      slotProps={{ textField: { fullWidth: true, variant: "outlined" } }} />
                  )} />
              </Grid>
            </Grid>

            <SectionTitle title="معلومات التواصل" />
            <Grid container spacing={2}>

              <Grid item md={3} xs={12}>
                <FieldLabel label="رقم الموبايل" required />
                <Controller name="mainMobile" control={control}
                  rules={{ required: "هذا الحقل مطلوب", pattern: { value: /^[0-9]+$/, message: "أرقام فقط" } }}
                  render={({ field }) => (
                    <TextField {...field} disabled={onLastFormPage} fullWidth variant="outlined"
                      inputProps={{ inputMode: "numeric" }}
                      error={!!errors.mainMobile} helperText={errors.mainMobile?.message}
                      FormHelperTextProps={{ sx: { textAlign: "start" } }} />
                  )} />
              </Grid>

              <Grid item md={3} xs={12}>
                <FieldLabel label="هاتف 1" />
                <Controller name="telephone1" control={control}
                  render={({ field }) => <TextField {...field} disabled={onLastFormPage} fullWidth variant="outlined" />} />
              </Grid>

              <Grid item md={3} xs={12}>
                <FieldLabel label="هاتف 2" />
                <Controller name="telephone2" control={control}
                  render={({ field }) => <TextField {...field} disabled={onLastFormPage} fullWidth variant="outlined" />} />
              </Grid>

              <Grid item md={3} xs={12}>
                <FieldLabel label="البريد الإلكتروني" />
                <Controller name="email" control={control}
                  rules={{
                    validate: (v) => {
                      const trimmed = v?.trim() ?? "";
                      if (!trimmed) return true;
                      return EMAIL_REGEX.test(trimmed) ? true : "بريد إلكتروني غير صحيح";
                    },
                  }}
                  render={({ field }) => (
                    <TextField {...field} disabled={onLastFormPage} type="email" fullWidth variant="outlined"
                      error={!!errors.email} helperText={errors.email?.message}
                      FormHelperTextProps={{ sx: { textAlign: "start" } }} />
                  )} />
              </Grid>

              <Grid item md={3} xs={12}>
                <FieldLabel label="فاكس" />
                <Controller name="fax" control={control}
                  render={({ field }) => <TextField {...field} disabled={onLastFormPage} fullWidth variant="outlined" />} />
              </Grid>

              <Grid item md={3} xs={12}>
                <FieldLabel label="صندوق البريد" />
                <Controller name="mailBox" control={control}
                  render={({ field }) => <TextField {...field} disabled={onLastFormPage} fullWidth variant="outlined" />} />
              </Grid>

              <Grid item md={3} xs={12}>
                <FieldLabel label="الموقع الإلكتروني" />
                <Controller name="website" control={control}
                  render={({ field }) => <TextField {...field} disabled={onLastFormPage} fullWidth variant="outlined" />} />
              </Grid>
            </Grid>

            <SectionTitle title="العنوان" />
            <Grid container spacing={2}>
              <Grid item md={3} xs={12}>
                <FieldLabel label="المدينة" />
                <Controller name="city" control={control}
                  render={({ field }) => <TextField {...field} disabled={onLastFormPage} fullWidth variant="outlined" />} />
              </Grid>
              <Grid item md={3} xs={12}>
                <FieldLabel label="الشارع" />
                <Controller name="street" control={control}
                  render={({ field }) => <TextField {...field} disabled={onLastFormPage} fullWidth variant="outlined" />} />
              </Grid>
              <Grid item md={3} xs={12}>
                <FieldLabel label="العنوان التفصيلي" />
                <Controller name="address" control={control}
                  render={({ field }) => <TextField {...field} disabled={onLastFormPage} fullWidth variant="outlined" />} />
              </Grid>
            </Grid>

          </Box>

          {/* ====== STEP 1: Owners ====== */}
          <Box sx={{ display: activeStep === 1 || onLastFormPage ? "block" : "none" }}>
            <SectionTitle title="الملاك" />

            {/* Add owner row — only on step 1 */}
            {activeStep === 1 && (
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item md={4} xs={12}>
                    <FieldLabel label="رقم الوثيقة (للمواطن)" />
                    <TextField
                      fullWidth variant="outlined" size="small"
                      value={respForm.docNumber}
                      onChange={(e) => setRespForm((p) => ({ ...p, docNumber: e.target.value, found: null, error: "" }))}
                      onKeyDown={(e) => e.key === "Enter" && searchCitizenForRole()}
                      placeholder="أدخل رقم الوثيقة ثم ابحث"
                    />
                  </Grid>
                  <Grid item md={2} xs={12}>
                    <Button
                      variant="outlined" fullWidth
                      startIcon={respForm.searching ? <CircularProgress size={16} /> : <SearchIcon />}
                      disabled={respForm.searching || !respForm.docNumber.trim()}
                      onClick={searchCitizenForRole}>
                      بحث
                    </Button>
                  </Grid>
                </Grid>

                {respForm.error && (
                  <Typography color="error" variant="caption" sx={{ mt: 1, display: "block" }}>{respForm.error}</Typography>
                )}

                {respForm.found && (
                  <Box sx={{ mt: 1.5, p: 1.5, border: "1px solid", borderColor: "success.light", borderRadius: 1, bgcolor: "success.50" }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>{respForm.found.fullName} ({respForm.found.documentNumber})</Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item md={3} xs={12}>
                        <TextField
                          select fullWidth size="small" label="الصفة / الدور"
                          value={respForm.role}
                          onChange={(e) => setRespForm((p) => ({ ...p, role: e.target.value }))}>
                          <MenuItem value=""><em>غير محدد</em></MenuItem>
                          <MenuItem value={1}>مالك</MenuItem>
                          <MenuItem value={2}>شريك</MenuItem>
                          <MenuItem value={3}>مستفيد</MenuItem>
                        </TextField>
                      </Grid>
                      <Grid item md={3} xs={12}>
                        <TextField
                          fullWidth size="small" label="نسبة الشراكة %" type="number"
                          inputProps={{ min: 0, max: 100, step: 0.01 }}
                          value={respForm.percentage}
                          onChange={(e) => setRespForm((p) => ({ ...p, percentage: e.target.value }))}
                        />
                      </Grid>
                      <Grid item md={3} xs={12}>
                        <FormControlLabel
                          control={
                            <Checkbox
                              checked={respForm.isMainContact}
                              onChange={(e) => setRespForm((p) => ({ ...p, isMainContact: e.target.checked }))}
                            />
                          }
                          label="جهة الاتصال الرئيسية"
                        />
                      </Grid>
                      <Grid item md={2} xs={12}>
                        <Button variant="contained" size="small" onClick={addResponsible}>إضافة</Button>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Box>
            )}

            {/* Owners list */}
            {responsibles.length === 0 && onLastFormPage && (
              <Typography color="text.secondary" sx={{ mb: 2 }}>لا يوجد ملاك أو مسؤولون مضافون</Typography>
            )}
            {responsibles.length === 0 && activeStep === 1 && (
              <Typography color="text.secondary" sx={{ mb: 2 }}>لم يتم إضافة أي ملاك بعد. أضف على الأقل مالكاً واحداً للاستمرار.</Typography>
            )}
            {responsibles.length > 0 && (
              <Box sx={{ mb: 2, overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>الاسم</TableCell>
                      <TableCell>رقم الوثيقة</TableCell>
                      <TableCell>الصفة</TableCell>
                      <TableCell>نسبة الشراكة %</TableCell>
                      <TableCell>جهة اتصال رئيسية</TableCell>
                      {activeStep === 1 && <TableCell />}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {responsibles.map((r) => (
                      <TableRow key={r.citizenId}>
                        <TableCell>{r.fullName}</TableCell>
                        <TableCell>{r.documentNumber}</TableCell>
                        <TableCell>
                          {r.responsibleRole === 1 ? "مالك" : r.responsibleRole === 2 ? "شريك" : r.responsibleRole === 3 ? "مستفيد" : "—"}
                        </TableCell>
                        <TableCell>{r.partnershipPercentage ?? "—"}</TableCell>
                        <TableCell>{r.isMainContact ? "نعم" : "لا"}</TableCell>
                        {activeStep === 1 && (
                          <TableCell>
                            <IconButton size="small" color="error"
                              onClick={() => setResponsibles((prev) => prev.filter((x) => x.citizenId !== r.citizenId))}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

          </Box>

          {/* ====== STEP 2: Delegates ====== */}
          <Box sx={{ display: activeStep === 2 || onLastFormPage ? "block" : "none" }}>
            <SectionTitle title="المفوّضون" />

            {activeStep === 2 && allowCurrentUserDelegate && currentUserDelegate?.delegateId && (
              <Box sx={{ mb: 2, p: 1.5, border: "1px solid", borderColor: "divider", borderRadius: 1 }}>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={includeCurrentUserDelegate}
                      onChange={(e) => setIncludeCurrentUserDelegate(e.target.checked)}
                    />
                  }
                  label="إضافتي كمفوّض لهذه الشركة (اختياري)"
                />
                <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                  {currentUserDelegate.fullName} - {currentUserDelegate.documentNumber}
                </Typography>
              </Box>
            )}

            {/* Add delegate row — only on step 2 */}
            {activeStep === 2 && (
              <Box sx={{ mb: 2 }}>
                <Grid container spacing={2} alignItems="flex-end">
                  <Grid item md={4} xs={12}>
                    <FieldLabel label="رقم الوثيقة (للمفوَّض)" />
                    <TextField
                      fullWidth variant="outlined" size="small"
                      value={delForm.docNumber}
                      onChange={(e) => setDelForm((p) => ({ ...p, docNumber: e.target.value, found: null, error: "" }))}
                      onKeyDown={(e) => e.key === "Enter" && searchCitizenForDelegation()}
                      placeholder="أدخل رقم الوثيقة ثم ابحث"
                    />
                  </Grid>
                  <Grid item md={2} xs={12}>
                    <Button
                      variant="outlined" fullWidth
                      startIcon={delForm.searching ? <CircularProgress size={16} /> : <SearchIcon />}
                      disabled={delForm.searching || !delForm.docNumber.trim()}
                      onClick={searchCitizenForDelegation}>
                      بحث
                    </Button>
                  </Grid>
                </Grid>

                {delForm.error && (
                  <Typography color="error" variant="caption" sx={{ mt: 1, display: "block" }}>{delForm.error}</Typography>
                )}

                {delForm.found && (
                  <Box sx={{ mt: 1.5, p: 1.5, border: "1px solid", borderColor: "success.light", borderRadius: 1 }}>
                    <Typography variant="body2" fontWeight="bold" sx={{ mb: 1 }}>{delForm.found.fullName} ({delForm.found.documentNumber})</Typography>
                    <Grid container spacing={2} alignItems="center">
                      <Grid item md={8} xs={12}></Grid>
                      <Grid item md={2} xs={12}>
                        <Button variant="contained" size="small" onClick={addDelegation}>إضافة</Button>
                      </Grid>
                    </Grid>
                  </Box>
                )}
              </Box>
            )}

            {/* Delegates list */}
            {effectiveDelegations.length === 0 && onLastFormPage && (
              <Typography color="text.secondary" sx={{ mb: 2 }}>لا يوجد مفوّضون مضافون</Typography>
            )}
            {effectiveDelegations.length === 0 && activeStep === 2 && (
              <Typography color="text.secondary" sx={{ mb: 2 }}>لم يتم إضافة أي مفوّض بعد. أضف على الأقل مفوّضاً واحداً للاستمرار.</Typography>
            )}
            {effectiveDelegations.length > 0 && (
              <Box sx={{ mb: 2, overflowX: "auto" }}>
                <Table size="small">
                  <TableHead>
                    <TableRow>
                      <TableCell>الاسم</TableCell>
                      <TableCell>رقم الوثيقة</TableCell>
                      {activeStep === 2 && <TableCell />}
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {effectiveDelegations.map((d) => (
                      <TableRow key={d.delegateId}>
                        <TableCell>
                          {d.fullName}
                          {d.fromCurrentUser && (
                            <Chip label="أنا" size="small" color="primary" variant="outlined" sx={{ ml: 1 }} />
                          )}
                        </TableCell>
                        <TableCell>{d.documentNumber}</TableCell>
                        {activeStep === 2 && !d.fromCurrentUser && (
                          <TableCell>
                            <IconButton size="small" color="error"
                              onClick={() => setDelegations((prev) => prev.filter((x) => x.delegateId !== d.delegateId))}>
                              <DeleteIcon fontSize="small" />
                            </IconButton>
                          </TableCell>
                        )}
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Box>
            )}

          </Box>

          {/* ====== STEP 3: Attachments ====== */}
          <Box sx={{ display: activeStep === 3 || onLastFormPage ? "block" : "none" }}>
            <SectionTitle title="المرفقات" />

            {/* Upload row — only shown on step 3 */}
            {activeStep === 3 && (
              <Grid container spacing={2} alignItems="flex-end" sx={{ mb: 2 }}>
                <Grid item md={4} xs={12}>
                  <FieldLabel label="نوع المرفق" />
                  <TextField
                    select fullWidth variant="outlined" size="small"
                    value={selectedAttachType}
                    disabled={attachmentTypes.length === 0}
                    onChange={(e) => { setSelectedAttachType(e.target.value); setAttachError(""); }}>
                    <MenuItem value="" disabled><em>اختر نوع المرفق</em></MenuItem>
                    {attachmentTypes.map((t) => (
                      <MenuItem key={t.id} value={t.id} sx={{ direction: "rtl" }}>{t.name}</MenuItem>
                    ))}
                  </TextField>
                </Grid>

                <Grid item md={4} xs={12}>
                  <input
                    type="file"
                    id="companyFileInput"
                    style={{ display: "none" }}
                    accept={validFileTypes.join(",")}
                    onChange={(e) => { handleFileSelected(e.target.files[0]); e.target.value = ""; }}
                  />
                  <Button
                    variant="outlined"
                    startIcon={<AttachFileIcon />}
                    disabled={attachmentTypes.length === 0}
                    onClick={() => {
                      if (!selectedAttachType) { setAttachError("الرجاء اختيار نوع المرفق أولاً"); return; }
                      document.getElementById("companyFileInput").click();
                    }}
                    fullWidth>
                    إرفاق ملف
                  </Button>
                </Grid>

                {attachmentTypes.length === 0 && (
                  <Grid item xs={12}>
                    <Typography color="warning.main" variant="caption">
                      لا توجد أنواع مرفقات مخصصة لتسجيل الشركات حالياً. يرجى التواصل مع البلدية.
                    </Typography>
                  </Grid>
                )}

                {attachError && (
                  <Grid item xs={12}>
                    <Typography color="error" variant="caption">{attachError}</Typography>
                  </Grid>
                )}
              </Grid>
            )}

            {/* Attachments list */}
            {attachments.length === 0 && onLastFormPage && (
              <Typography color="text.secondary" sx={{ mb: 2 }}>لا توجد مرفقات</Typography>
            )}
            {attachments.length > 0 && (
              <Stack spacing={1} sx={{ mb: 2 }}>
                {attachments.map((att) => (
                  <Stack key={att.typeId} direction="row" alignItems="center" spacing={1}
                    sx={{ border: "1px solid", borderColor: "divider", borderRadius: 1, p: 1 }}>
                    <CheckCircleIcon color="success" fontSize="small" />
                    <Chip label={att.typeName} size="small" color="primary" variant="outlined" />
                    <Typography variant="body2" sx={{ flex: 1, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
                      {att.fileObject.name}
                    </Typography>
                    <IconButton size="small" onClick={() => setPreviewFile({ fileObject: att.fileObject })}>
                      <VisibilityIcon fontSize="small" />
                    </IconButton>
                    {activeStep === 3 && (
                      <IconButton size="small" color="error" onClick={() => removeAttachment(att.typeId)}>
                        <DeleteIcon fontSize="small" />
                      </IconButton>
                    )}
                  </Stack>
                ))}
              </Stack>
            )}
          </Box>

          {/* ====== Navigation Buttons ====== */}
          {stepError && (
            <Typography color="error" sx={{ mt: 2, mb: 1, textAlign: "center", fontWeight: "bold" }}>{stepError}</Typography>
          )}
          <Stack direction="row" justifyContent="space-between" sx={{ mt: 4 }}>
            <Button variant="outlined" disabled={activeStep === 0} onClick={handleBack}>
              السابق
            </Button>
            <Button variant="contained" onClick={handleNext} disabled={isSubmitting}>
              {isSubmitting ? <CircularProgress size={22} color="inherit" /> :
                activeStep === stepsLength - 1 ? "إرسال الطلب" : "التالي"}
            </Button>
          </Stack>
        </form>
      </LocalizationProvider>

      {previewFile && (
        <PreviewAttachmentDialog
          open={Boolean(previewFile)}
          attachmentDetails={previewFile}
          handleClose={() => setPreviewFile(null)}
        />
      )}

      <MessageModal
        openModal={openSuccessModal}
        onCloseModal={() => navigate("/login")}
        buttonText="حسناً، انتقل لتسجيل الدخول"
        interfaceIcon={<CheckCircleIcon color="primary" sx={{ height: 80, width: 80 }} />}
        message="تم استلام طلب تسجيل الشركة بنجاح. سيقوم موظفو البلدية بمراجعة الطلب وتفعيل الحساب والتواصل معكم قريباً."
      />

      <MessageModal
        openModal={openFailureModal}
        onCloseModal={() => setOpenFailureModal(false)}
        buttonText="إغلاق"
        interfaceIcon={<CancelIcon color="primary" sx={{ height: 80, width: 80 }} />}
        message={errorMessage}
      />
    </Box>
  );
};

export default CompanySignupForm;
